import { CSSProperties, ReactNode, SyntheticEvent, useEffect, useRef, useState } from 'react'
import { ControlSize, TextFormat } from 'types'
import { formatText } from 'utils'
import { Input, Textarea, Wrapper } from './style'

export type TextInputProps = {
    autoComplete?: string
    autoCorrect?: boolean
    autoFocus?: boolean
    children?: ReactNode
    className?: string
    color?: string
    defaultValue?: string
    disabled?: boolean
    forceDisable1Password?: boolean
    format?: TextFormat
    maxLength?: number
    multiline?: boolean
    placeholder?: string
    selectOnFocus?: boolean
    size?: ControlSize
    spellCheck?: boolean
    style?: CSSProperties
    textColor?: string
    value?: string
    onBlur?: () => void
    onChange?: (value: string) => void
    onEnterKeyPress?: () => void
    onFocus?: () => void
}

const TextInput = ({
    autoComplete,
    autoCorrect = false,
    autoFocus,
    children,
    className,
    color = '#fff',
    defaultValue = '',
    disabled = false,
    forceDisable1Password = false,
    format,
    maxLength,
    multiline = false,
    placeholder,
    selectOnFocus = false,
    size = 'small',
    spellCheck = false,
    style = {},
    textColor,
    value: valueProp,
    onBlur,
    onChange,
    onEnterKeyPress,
    onFocus,
}: TextInputProps) => {
    const initialValueRef = useRef<string | undefined>(valueProp || defaultValue)
    const inputRef = useRef<HTMLInputElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const [value, setValue] = useState<string>(valueProp || defaultValue)
    const [textareaHeight, setTextareaHeight] = useState<number | undefined>()

    const formatInputValue = () => {
        if (value.length > 0 && format) {
            setValue(formatText(value, format))
        }
    }

    const onInputBlur = () => {
        formatInputValue()
        onBlur?.()
    }

    const onInputChange = (e: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement, Event>) => {
        setValue(e.currentTarget.value)
    }

    const onInputFocus = (e: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement, Event>) => {
        onFocus?.()

        if (selectOnFocus) {
            e.currentTarget.select()
        }
    }

    const onInputKeyDown = (e: SyntheticEvent<HTMLElement, KeyboardEvent>) => {
        if (e.nativeEvent.key !== 'Enter') {
            return
        }

        if (multiline && !!value) {
            if (!onEnterKeyPress || e.nativeEvent.shiftKey) {
                return
            }
        }

        e.preventDefault()
        formatInputValue()
        onEnterKeyPress?.()
    }

    const inputProps = {
        autoComplete,
        autoCorrect: autoCorrect ? 'on' : 'off',
        maxLength,
        placeholder,
        spellCheck,
        value,
        'data-1p-ignore': forceDisable1Password ? true : undefined,
        onBlur: onInputBlur,
        onChange: onInputChange,
        onFocus: onInputFocus,
        onKeyDown: onInputKeyDown,
    }

    useEffect(() => {
        if (value !== initialValueRef.current) {
            initialValueRef.current = undefined

            if (format) {
                onChange?.(formatText(value, format))
            } else {
                onChange?.(value)
            }
        }

        if (multiline && textareaRef.current) {
            textareaRef.current.style.height = '0px'
            setTextareaHeight(textareaRef.current.scrollHeight)
            textareaRef.current.style.height = ''
        }
    }, [value])

    useEffect(() => {
        if (typeof valueProp === 'string' && valueProp !== value) {
            setValue(valueProp)
        }
    }, [valueProp])

    useEffect(() => {
        if (autoFocus) {
            inputRef.current?.focus()
            textareaRef.current?.focus()
        }
    }, [autoFocus])

    return (
        <Wrapper
            className={className}
            controlColor={color}
            controlSize={size}
            disabled={disabled}
            style={{ ...style, height: textareaHeight }}
            textColor={textColor}
        >
            {multiline ? <Textarea {...inputProps} ref={textareaRef} /> : <Input {...inputProps} ref={inputRef} />}
            {children}
        </Wrapper>
    )
}

export default TextInput
